import { SpatialMap, WorldPosition } from './protocol';

const distance = (a: WorldPosition, b: WorldPosition) => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
const key = (p: WorldPosition) => [p.x, p.y, p.z].map(n => Math.round(n * 100)).join(',');

/** Triangles connected by shared edges, including duplicated vertices across tiles. */
export function createNavigator(map: SpatialMap) {
  const triangles = [] as { points: WorldPosition[]; center: WorldPosition; links: Map<number, WorldPosition> }[];
  const edges = new Map<string, { triangle: number; midpoint: WorldPosition }>();
  for (let i = 0; i < map.indices.length; i += 3) {
    const points = map.indices.slice(i, i + 3).map(index => map.vertices[index]);
    if (points.length !== 3 || points.some(p => !p)) continue;
    const triangle = triangles.length;
    const center = { x: 0, y: 0, z: 0 };
    points.forEach(p => { center.x += p.x / 3; center.y += p.y / 3; center.z += p.z / 3; });
    triangles.push({ points, center, links: new Map() });
    points.forEach((a, j) => {
      const b = points[(j + 1) % 3];
      const edge = [key(a), key(b)].sort().join('|');
      const midpoint = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, z: (a.z + b.z) / 2 };
      const previous = edges.get(edge);
      if (previous) {
        triangles[previous.triangle].links.set(triangle, midpoint);
        triangles[triangle].links.set(previous.triangle, midpoint);
      } else edges.set(edge, { triangle, midpoint });
    });
  }
  const locate = (point: WorldPosition) => triangles.findIndex(({ points: [a, b, c] }) => {
    const denom = (b.z - c.z) * (a.x - c.x) + (c.x - b.x) * (a.z - c.z);
    if (Math.abs(denom) < .00001) return false;
    const u = ((b.z - c.z) * (point.x - c.x) + (c.x - b.x) * (point.z - c.z)) / denom;
    const v = ((c.z - a.z) * (point.x - c.x) + (a.x - c.x) * (point.z - c.z)) / denom;
    return u >= -.0001 && v >= -.0001 && u + v <= 1.0001 && Math.abs(point.y - (u * a.y + v * b.y + (1 - u - v) * c.y)) < .65;
  });
  return (from: WorldPosition, to: WorldPosition): WorldPosition[] => {
    const start = locate(from), end = locate(to);
    if (start < 0 || end < 0) return [];
    const open = new Set([start]), cost = new Map([[start, 0]]), parent = new Map<number, number>();
    while (open.size) {
      const current = Array.from(open).sort((a, b) => (cost.get(a)! + distance(triangles[a].center, to)) - (cost.get(b)! + distance(triangles[b].center, to)))[0];
      if (current === end) {
        const path: WorldPosition[] = [to];
        let next = end;
        while (parent.has(next)) {
          const prev = parent.get(next)!;
          path.unshift(triangles[next].links.get(prev)!);
          next = prev;
        }
        return path;
      }
      open.delete(current);
      triangles[current].links.forEach((_, next) => {
        const candidate = cost.get(current)! + distance(triangles[current].center, triangles[next].center);
        if (candidate >= (cost.get(next) ?? Infinity)) return;
        cost.set(next, candidate); parent.set(next, current); open.add(next);
      });
    }
    return [];
  };
}
